import { BI_EVENT_AD_ERROR, BI_EVENT_AD_IMPRESSION, BI_EVENT_AD_LOADED } from '@/constants/bi-sdk'

export const AD_FORMATS = ['banner', 'video', 'reward_video', 'overlay', 'reward_overlay'] as const

export type AdFormat = (typeof AD_FORMATS)[number]

export const PROVIDER_AD_ERROR_CODES = [
  'PROVIDER_NO_FILL',
  'PROVIDER_LOAD_ERROR',
  'PROVIDER_PLAYBACK_ERROR',
  'PROVIDER_TIMEOUT',
  'PROVIDER_UNSUPPORTED_ENVIRONMENT',
  'PROVIDER_UNKNOWN_ERROR',
] as const

export type ProviderAdErrorCode = (typeof PROVIDER_AD_ERROR_CODES)[number]

export type NormalizedAdLifecycleEvent =
  | typeof BI_EVENT_AD_LOADED
  | typeof BI_EVENT_AD_IMPRESSION
  | typeof BI_EVENT_AD_ERROR

const AD_LIFECYCLE_EVENT_MAPPINGS: Record<
  AdFormat,
  Readonly<Record<string, Exclude<NormalizedAdLifecycleEvent, typeof BI_EVENT_AD_ERROR>>>
> = {
  banner: {
    GPT_SLOT_RENDERED: BI_EVENT_AD_LOADED,
    GPT_IMPRESSION_VIEWABLE: BI_EVENT_AD_IMPRESSION,
  },
  overlay: {
    GPT_SLOT_READY: BI_EVENT_AD_LOADED,
    GPT_IMPRESSION_VIEWABLE: BI_EVENT_AD_IMPRESSION,
  },
  reward_overlay: {
    GPT_SLOT_READY: BI_EVENT_AD_LOADED,
    GPT_IMPRESSION_VIEWABLE: BI_EVENT_AD_IMPRESSION,
  },
  video: {
    IMA_LOADED: BI_EVENT_AD_LOADED,
    IMA_STARTED: BI_EVENT_AD_IMPRESSION,
  },
  reward_video: {
    IMA_LOADED: BI_EVENT_AD_LOADED,
    IMA_STARTED: BI_EVENT_AD_IMPRESSION,
  },
}

export const isAdFormat = (value: unknown): value is AdFormat =>
  typeof value === 'string' && (AD_FORMATS as readonly string[]).includes(value)

export const isProviderAdErrorCode = (value: unknown): value is ProviderAdErrorCode =>
  typeof value === 'string' && (PROVIDER_AD_ERROR_CODES as readonly string[]).includes(value)

export const isMatchingAdAttemptFormat = (
  issuedAdFormat: AdFormat | undefined,
  eventAdFormat: unknown,
): eventAdFormat is AdFormat => issuedAdFormat !== undefined && eventAdFormat === issuedAdFormat

export const normalizeAdSdkLifecycleEvent = ({
  adFormat,
  eventType,
  errorCode,
}: {
  adFormat: AdFormat
  eventType: string
  errorCode: unknown
}): NormalizedAdLifecycleEvent | null => {
  const isImaAdFormat = adFormat === 'video' || adFormat === 'reward_video'
  const expectedProviderErrorEvent = isImaAdFormat ? 'IMA_AD_ERROR' : 'GPT_AD_ERROR'

  if (eventType === expectedProviderErrorEvent && isProviderAdErrorCode(errorCode)) {
    return BI_EVENT_AD_ERROR
  }

  return AD_LIFECYCLE_EVENT_MAPPINGS[adFormat][eventType] ?? null
}
